import { ChevronDown, Pencil, Trash } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDesignSystem } from "../../context/designSystemContext";
import { pSBC } from "../../utils/pSBC";

export interface CollapsibleProps {
  header: React.ReactNode;
  children: React.ReactNode;
  editable: boolean;
  deletable?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onOpen: () => void;
  onDelete?: () => void;
}

const Collapsible: React.FC<CollapsibleProps> = ({
  editable = false,
  deletable = false,
  header,
  isOpen = false,
  children,
  onClose,
  onEdit,
  onOpen,
  onDelete,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isOpen);

  const { designSystem } = useDesignSystem();

  useEffect(() => {
    setIsExpanded(isOpen);
  }, [isOpen]);

  const toggleSize = () => {
    isExpanded ? onClose() : onOpen();
    setIsExpanded((prev) => !prev);
  };

  const handleEdit = () => {
    if (!isExpanded) {
      onOpen();
      setIsExpanded(true);
    }

    onEdit();
  };

  return (
    <div className="border rounded-md shadow-custom-shadow overflow-hidden bg-white">
      <div className="flex justify-between items-center p-4">
        {header}
        <div className="flex mt-1 gap-2">
          {editable && (
            <button aria-label="edit" onClick={handleEdit} className="mb-1 gap-2">
              <Pencil className="size-[18px]" color={`${pSBC(0.9, designSystem?.cssBasics.primaryColor)}`} />
            </button>
          )}

          {deletable && (
            <button aria-label="delete" onClick={onDelete} className="mb-1 gap-2">
              <Trash className="size-[18px]" color="#0C7878" />
            </button>
          )}

          <button aria-label="toggle" onClick={toggleSize} className="gap-2">
            <ChevronDown
              className={`size-7 transition-transform duration-300 ${
                isExpanded ? "rotate-180" : "rotate-0"
              }`}
            />
          </button>
        </div>
      </div>
      {isExpanded && <div className="p-4">{children}</div>}
    </div>
  );
};

export default Collapsible;
